'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { usePathname } from 'next/navigation';

import { useLocalStorage } from '@/hooks/useLocalStorage';

import { AppResolutionType } from '@/enums';
import { StoredDataKeyName } from '@/services/auth';
import { UserInformationQueryResponse } from '@/services/graphQL';
import { Language, Maturity } from '@/services/graphQL/api/generated/types_web';
import { getLocalization } from '@/utils/localization';
import { Localization } from '@/utils/localization/localization';
import { normalizeDeviceLanguage } from '@/utils/normalizeDeviceLanguage';

export type PageState = {
  page: string;
  state?: any;
};

type GlobalContextType = {
  currentPage?: PageState;
  showHeader: boolean;
  showFooter: boolean;
  currentResolution: AppResolutionType | undefined;
  userLanguage: Language;
  userMaturity: Maturity;
  playbackLanguage: string[] | null;
  localization: Localization;
  toggleHeader: (show: boolean) => void;
  toggleFooter: (show: boolean) => void;
  setLanguage: (language: Language) => void;
  setPlaybackLanguage: (language: string[]) => void;
  setMaturity: (maturity: Maturity) => void;
  setCurrentPageState: (currentPage: PageState | undefined) => void;
  setUserExperienceInformation: (
    userInformationData?: UserInformationQueryResponse
  ) => void;
  setUserSubscriptionInformation: (
    subscriptionInformationData?: BDUSubscriptionInformationType
  ) => void;
  pageStack: PageState[];
  setPageStack: React.Dispatch<React.SetStateAction<PageState[]>>;
  mParticleSessionStarted: boolean;
  setMParticleSessionStarted: (sessionStarted: boolean) => void;
};

export type BDUSubscriptionInformationType = {
  providerDisplayName: string;
  type: string;
};
// Create the context
const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

// Provide the context to components
export const GlobalConfigProvider = ({
  language,
  currentResolution,
  children,
}: {
  language: Language;
  currentResolution?: AppResolutionType;
  children: React.ReactNode;
}) => {
  const pathname = usePathname();
  const [languagePreference, _, deleteLanguagePreference] =
    useLocalStorage<string>('languagePreference');
  const [showHeader, setShowHeader] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [currentPage, setCurrentPage] = useState<PageState>();
  const [pendingUserLanguage, setPendingUserLanguage] = useState<Language>(
    languagePreference
      ? normalizeDeviceLanguage(languagePreference)
      : language || Language.En
  );
  const [playbackLanguage, setPlaybackLanguage] = useState<string[] | null>(null);

  // When the platform resolves a device language asynchronously (e.g. Xfinity via Firebolt)
  // the `language` prop updates after mount. Sync it into pendingUserLanguage so the next
  // navigation picks up the correct locale. Skipped if the profile has a stored preference.
  useEffect(() => {
    if (!languagePreference) {
      setPendingUserLanguage(language);
    }
  }, [language]);

  const [userMaturity, setUserMaturity] = useState<Maturity | null>(null);
  const [pageStack, setPageStack] = useState<PageState[]>([]);
  const [mParticleSessionStarted, setMParticleSessionStarted] = useState(false);

  const toggleHeader = (show: boolean) => setShowHeader(show);
  const toggleFooter = (show: boolean) => setShowFooter(show);

  const userLanguage = useMemo(
    () =>
      languagePreference
        ? normalizeDeviceLanguage(languagePreference)
        : pendingUserLanguage || Language.En,
    [pathname]
  );

  const localization = getLocalization(userLanguage);

  const setCurrentPageState = useCallback((currentPage: PageState | undefined) => {
    setCurrentPage(currentPage || undefined);
  }, []);

  const setUserLanguageHandler = (language: Language) => {
    deleteLanguagePreference();
    setPendingUserLanguage(language);
  };

  const setUserExperienceInformation = (
    userInformationData?: UserInformationQueryResponse
  ) => {
    if (
      userInformationData?.userAccountInfo?.experiencesPage?.experiences &&
      userInformationData?.userAccountInfo?.experiencesPage?.experiences?.length > 0
    ) {
      let experiences =
        userInformationData?.userAccountInfo?.experiencesPage?.experiences;
      let userExperiencesInformation = '';

      let ultraHDEnable = false;

      for (let i = 0; i < experiences.length; i++) {
        if (
          experiences[i].contentPolicySKUs &&
          experiences[i].contentPolicySKUs!.length > 0
        ) {
          if (experiences[i].contentPolicySKUs) {
            userExperiencesInformation += `${experiences[i].contentPolicySKUs?.join(', ')}`;
          }
        }

        if (
          experiences[i]?.featurePolicies &&
          experiences[i]?.featurePolicies!.length > 0
        ) {
          const ultraHDFeature = experiences[i].featurePolicies?.find(
            (e) => e === 'playback_quality:4k'
          );
          if (ultraHDFeature) {
            ultraHDEnable = true;
          }
        }
      }
      localStorage.setItem(StoredDataKeyName.USEREXPERIENCES, userExperiencesInformation);
      localStorage.setItem(StoredDataKeyName.EXPERIENCESKU, experiences[0].experienceSKU);
      if (ultraHDEnable) {
        localStorage.setItem(StoredDataKeyName.ULTRAHDENABLED, 'enabled');
      }
    } else {
      localStorage.removeItem(StoredDataKeyName.USEREXPERIENCES);
      localStorage.removeItem(StoredDataKeyName.EXPERIENCESKU);
      localStorage.removeItem(StoredDataKeyName.ULTRAHDENABLED);
    }
  };

  const setUserSubscriptionInformation = (
    subscriptionInformationData?: BDUSubscriptionInformationType
  ) => {
    if (subscriptionInformationData && subscriptionInformationData?.providerDisplayName) {
      localStorage.setItem(
        StoredDataKeyName.SUBSCRIPTION,
        subscriptionInformationData?.providerDisplayName
      );
    } else {
      localStorage.removeItem(StoredDataKeyName.SUBSCRIPTION);
    }
  };

  return (
    <GlobalContext.Provider
      value={{
        showHeader,
        showFooter,
        currentResolution,
        toggleHeader,
        toggleFooter,
        localization,
        setLanguage: setUserLanguageHandler,
        setPlaybackLanguage: setPlaybackLanguage,
        setMaturity: setUserMaturity,
        setUserExperienceInformation,
        setUserSubscriptionInformation,
        userLanguage,
        playbackLanguage,
        userMaturity: userMaturity || Maturity.Adult,
        currentPage,
        setCurrentPageState,
        pageStack,
        setPageStack,
        mParticleSessionStarted,
        setMParticleSessionStarted,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

// Create a custom hook for easy access to the context
export const useGlobalConfig = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalConfig must be used within a GlobalConfigProvider');
  }
  return context;
};
